import React, { useEffect, useState, useRef, useCallback } from 'react';
import { fetchChart } from '../services/priceService';
import { useFreemium } from '../hooks/useFreemium';
import './PriceChart.css';

const PERIODS = ['7T', '1M', '6M', '1J', '5J'];
const PERIOD_LABELS = { '7T': '7 Tage', '1M': '1 Monat', '6M': '6 Monate', '1J': '1 Jahr', '5J': '5 Jahre' };

function PriceChart({ ticker, change7d, onHoverPrice, onHoverChange, onPeriodChange, rebound }) {
  const [points, setPoints] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hover, setHover] = useState(null);
  const [period, setPeriod] = useState('1M');
  const svgRef = useRef(null);
  const cache = useRef({}); // Cache pro Ticker+Period
  const { canAccess } = useFreemium();

  const loadChart = useCallback(async (p) => {
    const key = `${ticker}-${p}`;
    if (cache.current[key]) {
      setPoints(cache.current[key]);
      return;
    }
    setLoading(true);
    try {
      const d = await fetchChart(ticker, p);
      const pts = d.points || [];
      cache.current[key] = pts;
      setPoints(pts);
    } catch {
      setPoints([]);
    } finally {
      setLoading(false);
    }
  }, [ticker]);

  useEffect(() => {
    cache.current = {};
    loadChart(period);
  }, [ticker, loadChart, period]);

  const handlePeriodChange = (p) => {
    setPeriod(p);
    onPeriodChange?.(p);
    loadChart(p);
  };

  const closes = points.map(p => p.close);
  const firstClose = closes[0] ?? 0;
  const lastClose = closes[closes.length - 1] ?? 0;
  const periodChangePct = firstClose ? ((lastClose - firstClose) / firstClose) * 100 : 0;

  // Emit period change whenever chart data changes (must be before early returns)
  useEffect(() => {
    if (points.length >= 2) {
      onHoverChange?.(periodChangePct);
      onPeriodChange?.(period);
    }
  }, [ticker, period, points.length]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) return <div className="chart-skeleton shimmer" />;
  if (points.length < 2) return (
    <div className="chart-wrap" style={{ textAlign: 'center', padding: '24px 0', color: 'var(--color-text-muted)', fontSize: 13 }}>
      Keine Chartdaten verfügbar
    </div>
  );

  const W = 360, H = 130, PAD = 10;
  const min = Math.min(...closes);
  const max = Math.max(...closes);
  const range = max - min || 1;

  const toX = (i) => PAD + (i / (closes.length - 1)) * (W - PAD * 2);
  const toY = (v) => H - PAD - ((v - min) / range) * (H - PAD * 2);

  const pathD = closes.map((v, i) => `${i === 0 ? 'M' : 'L'}${toX(i).toFixed(1)},${toY(v).toFixed(1)}`).join(' ');
  const fillD = `${pathD} L${toX(closes.length - 1).toFixed(1)},${H} L${PAD},${H} Z`;

  const up = lastClose >= firstClose;
  const lineColor = rebound ? '#ff6b35' : (up ? '#16a34a' : '#bc000a');
  const gradId = `grad-${ticker}-${period}`;

  const handlePointerMove = (e) => {
    const svg = svgRef.current;
    if (!svg) return;
    const rect = svg.getBoundingClientRect();
    const relX = (e.clientX - rect.left) / rect.width;
    const idx = Math.round(Math.max(0, Math.min(1, relX)) * (closes.length - 1));
    const value = closes[idx];
    setHover({ idx, x: toX(idx), y: toY(value), value });
    onHoverPrice?.(value);
    onHoverChange?.(firstClose ? ((value - firstClose) / firstClose) * 100 : 0);
  };

  const handlePointerLeave = () => {
    setHover(null);
    onHoverPrice?.(null);
    onHoverChange?.(periodChangePct);
  };

  return (
    <div className="chart-wrap">
      <svg
        ref={svgRef}
        viewBox={`0 0 ${W} ${H}`}
        preserveAspectRatio="none"
        className="chart-svg"
        style={{ touchAction: 'none', cursor: hover ? 'crosshair' : 'default' }}
        onPointerMove={handlePointerMove}
        onPointerLeave={handlePointerLeave}
      >
        <defs>
          <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%"   stopColor={lineColor} stopOpacity="0.3" />
            <stop offset="100%" stopColor={lineColor} stopOpacity="0" />
          </linearGradient>
        </defs>
        <path d={fillD} fill={`url(#${gradId})`} />
        <path d={pathD} fill="none" stroke={lineColor} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />

        {hover && (
          <>
            <line
              x1={hover.x} y1={PAD}
              x2={hover.x} y2={H - PAD}
              stroke="rgba(0,0,0,0.15)"
              strokeWidth="1"
              strokeDasharray="3,2"
            />
            <circle cx={hover.x} cy={hover.y} r="4" fill={lineColor} stroke="#fff" strokeWidth="1.5" />
          </>
        )}
      </svg>

      <div className="chart-labels">
        {hover ? (
          <>
            <span>{points[hover.idx]?.date?.slice(5)}</span>
            <span style={{ opacity: 0 }}>·</span>
            <span style={{ opacity: 0 }}>·</span>
          </>
        ) : (
          <>
            <span>{points[0]?.date?.slice(5)}</span>
            <span>{PERIOD_LABELS[period]}</span>
            <span>{points[points.length - 1]?.date?.slice(5)}</span>
          </>
        )}
      </div>

      <div className="chart-period-tabs">
        {PERIODS.map(p => {
          const periodKey = p === '6M' ? 'chart_6mo' : p === '1J' ? 'chart_1y' : p === '5J' ? 'chart_5y' : null;
          const locked = periodKey && !canAccess(periodKey);
          return (
            <button
              key={p}
              className={`chart-period-tab ${p === period ? 'active' : ''} ${locked ? 'locked' : ''}`}
              onClick={() => !locked && handlePeriodChange(p)}
              disabled={locked}
            >
              {locked ? `🔒 ${p}` : p}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default PriceChart;
